import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { OnboardingButtonOutlined, OnboardingButtonExternalLinkOutlined } from "../components/OnboardingButton";
import {
  BodyShortLargeStrong,
  LabelMedium,
  SmallLink,
} from "./OnboardingTypography";
import { getSigningStatus, initiateDocumentSigning } from "../services/onboardingApiService";
import {
  OnboardingCard,
  MessageBox,
  FlexColumn,
  FlexRow,
  CompletedSection,
  THEME,
} from "./OnboardingLayoutComponents";

// Removed - now using OnboardingCard from OnboardingLayoutComponents

const ErrorText = styled.div`
  color: ${THEME.colors.error};
  font-size: 14px;
  margin-top: ${THEME.spacing.sm};
`;

const RetryButton = styled.button`
  background: none;
  border: 1px solid ${THEME.colors.error};
  color: ${THEME.colors.error};
  padding: ${THEME.spacing.xs} ${THEME.spacing.sm};
  border-radius: ${THEME.borderRadius.sm};
  cursor: pointer;
  font-size: 12px;
  margin-top: ${THEME.spacing.sm};
`;

const CenterDot = styled.span`
  font-size: 12px;
  color: #0073e6;
  position: relative;
  z-index: 2;
`;

const PulseDot = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  font-size: 12px;
  color: #0073e6;
  z-index: 1;
  animation: pulseRing 2s infinite;

  @keyframes pulseRing {
    0% {
      opacity: 0;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.5);
    }
    100% {
      opacity: 0;
      transform: scale(2);
    }
  }
`;

const ResponsivePollingRow = styled(FlexRow)`
  justify-content: flex-start;

  @media (min-width: 576px) {
    justify-content: flex-end;
  }
`;

const PollingIndicator = ({ pollingActive }) => {
  if (!pollingActive) return null;

  return (
    <ResponsivePollingRow alignItems="center" gap={THEME.spacing.sm}>
      <CenterDot className="fa fa-circle">
        <PulseDot className="fa fa-circle" />
      </CenterDot>
      <LabelMedium style={{ color: THEME.colors.textMuted }}>
        Venter på signering
      </LabelMedium>
    </ResponsivePollingRow>
  );
};

// Constants
const POLL_INTERVAL_MS = 10000;

// Custom hooks
const useSigningState = (hasSignedAgreement, signingStatus) => {
  return hasSignedAgreement || signingStatus === "Completed";
};

const useSigningPoller = (pollingActive, onSignatureChange, onError) => {
  useEffect(() => {
    if (!pollingActive) return;

    const pollForStatus = async () => {
      try {
        const statusResponse = await getSigningStatus();
        const currentStatus = statusResponse.data.status;

        if (currentStatus === "Completed") {
          onSignatureChange({
            hasSignedAgreement: true,
            signingStatus: currentStatus,
            signedDocumentUrl: statusResponse.data.signedDocumentUrl,
            completedDate: statusResponse.data.completedDate,
          });
          return true; // Stop polling
        }

        if (currentStatus === "Failed") {
          onError("Signering feilet. Vennligst prøv igjen.");
          return true; // Stop polling
        }

        return false; // Continue polling
      } catch (networkError) {
        console.log("Network error during polling, will retry:", networkError);
        return false; // Continue polling on network errors
      }
    };

    const pollInterval = setInterval(async () => {
      const shouldStop = await pollForStatus();
      // No need to close window since we're not opening one automatically
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollInterval);
    };
  }, [pollingActive, onSignatureChange, onError]);
};

// UI Components
const SignedAgreementView = () => (
  <FlexRow flex="1" alignItems="center" justifyContent="space-between">
    <FlexColumn flex="1" gap={THEME.spacing.md}>
      <FlexRow alignItems="baseline" gap={THEME.spacing.sm}>
        <span
          className="fa fa-check-circle"
          style={{ fontSize: "20px", color: "#000000" }}
        />
        <BodyShortLargeStrong>
          Steg 1: Les og signer brukeravtalen
        </BodyShortLargeStrong>
      </FlexRow>
      <SmallLink>
        Avtale signert {new Date().toLocaleDateString("no-NO")}
      </SmallLink>
    </FlexColumn>
    <span
      className="fa fa-check-circle"
      style={{ fontSize: "40px", color: "#09AF06" }}
    />
  </FlexRow>
);

const PendingSignatureView = ({
  error,
  onRetry,
  signingUrl,
  agreementDocumentUrl,
  signingInitLoading,
  signingInitError,
  popupBlocked,
  handleSignClick,
}) => (
  <FlexRow
    wrap="wrap"
    flex="1"
    alignItems="flex-start"
    justifyContent="space-between"
    gap={THEME.spacing.lg}
  >
    <FlexColumn
      gap={THEME.spacing.md}
      style={{
        flex: "1 1 250px",
        minWidth: "200px",
      }}
    >
      <FlexRow alignItems="baseline" gap={THEME.spacing.sm}>
        <span
          className="fa fa-arrow-circle-right"
          style={{ fontSize: "20px", color: THEME.colors.primaryDark }}
        />
        <BodyShortLargeStrong>
          Steg 1: Les og signer brukeravtalen
        </BodyShortLargeStrong>
      </FlexRow>
      <a
        href={agreementDocumentUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontFamily: "Manrope",
          fontSize: "16px",
          fontWeight: "400",
          lineHeight: "24px",
          letterSpacing: "0.032px",
          margin: "0",
          color: THEME.colors.text,
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {agreementDocumentUrl
          ? agreementDocumentUrl.split("/").pop()
          : "Document"}
      </a>

      {error && (
        <ErrorText>
          {error}
          <RetryButton onClick={onRetry}>Prøv igjen</RetryButton>
        </ErrorText>
      )}

      {signingInitError && (
        <ErrorText>
          {signingInitError}
        </ErrorText>
      )}

      {popupBlocked && (
        <MessageBox variant="warning" style={{ marginTop: THEME.spacing.sm }}>
          <LabelMedium>
            Pop-up ble blokkert av nettleseren. Vennligst tillat pop-ups for denne siden og klikk "Signer" på nytt.
          </LabelMedium>
        </MessageBox>
      )}
    </FlexColumn>

    <div style={{ flex: "0 0 auto", alignSelf: "flex-start" }}>
      <OnboardingButtonOutlined
        onClick={handleSignClick}
        disabled={signingInitLoading || (!signingUrl && !agreementDocumentUrl)}
      >
        <LabelMedium>
          {signingInitLoading ? (
            <>
              <span className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }} />
              Forbereder...
            </>
          ) : "Signer"}
        </LabelMedium>
      </OnboardingButtonOutlined>
    </div>
  </FlexRow>
);

// Main component
const OnboardingSignUserAgreement = ({
  hasSignedAgreement = false,
  onSignatureChange = null,
  signingStatus = null,
  signingUrl = null,
  agreementDocumentUrl = null,
  signingInitLoading = false,
  signingInitError = null,
}) => {
  const [error, setError] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);

  const isSigningComplete = useSigningState(hasSignedAgreement, signingStatus);

  // Auto-start polling when status is Pending
  useEffect(() => {
    if (signingStatus === "Pending") {
      setPollingActive(true);
    }
  }, [signingStatus]);

  const handleSigningComplete = useCallback(
    (signingData) => {
      setPollingActive(false);
      if (onSignatureChange) {
        onSignatureChange(signingData);
      }
    },
    [onSignatureChange]
  );

  const handleSigningError = useCallback((errorMessage) => {
    setPollingActive(false);
    setError(errorMessage);
  }, []);

  useSigningPoller(pollingActive, handleSigningComplete, handleSigningError);

  const handleRetry = () => {
    setError(null);
    setPollingActive(true);
  };

  const handleSignClick = async () => {
    setPopupBlocked(false);

    // If signing URL exists, open it
    if (signingUrl) {
      const popup = window.open(signingUrl, '_blank', 'noopener,noreferrer');

      // Detect if popup was blocked
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        setPopupBlocked(true);
      }
      return;
    }

    // If no signing URL, initiate signing first (manual retry case)
    // Note: Parent component handles automatic initiation on page load
    if (!signingInitLoading && agreementDocumentUrl) {
      try {
        const response = await initiateDocumentSigning(agreementDocumentUrl);

        // Update parent state with new signing URL
        if (onSignatureChange) {
          onSignatureChange({
            signingUrl: response.data.signingUrl,
            signingStatus: response.data.status,
            envelopeId: response.data.envelopeId
          });
        }

        // Auto-open the signing URL
        const popup = window.open(response.data.signingUrl, '_blank', 'noopener,noreferrer');

        // Detect if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          setPopupBlocked(true);
        }

        // Start polling for signing completion
        setPollingActive(true);
      } catch (initiationError) {
        console.error('Manual signing initiation failed:', initiationError);
        // Error is already handled by parent, just log here
      }
    }
  };

  return (
    <OnboardingCard padding={THEME.spacing.xxl} gap={THEME.spacing.xxl}>
      {isSigningComplete ? (
        <SignedAgreementView />
      ) : (
        <FlexColumn gap={THEME.spacing.lg}>
          <PendingSignatureView
            error={error}
            onRetry={handleRetry}
            signingUrl={signingUrl}
            agreementDocumentUrl={agreementDocumentUrl}
            signingInitLoading={signingInitLoading}
            signingInitError={signingInitError}
            popupBlocked={popupBlocked}
            handleSignClick={handleSignClick}
          />
          <PollingIndicator pollingActive={pollingActive} />
        </FlexColumn>
      )}
    </OnboardingCard>
  );
};

export default OnboardingSignUserAgreement;
